import { Icon as ChakraUiIcon, IconProps } from '@chakra-ui/react';
import React from 'react';

export type FontawesomeIconType = 'regular' | 'solid' | 'brand';

const iconSizes = ['md', 'lg', 'medium', 'large'] as const;

const isIconSize = (size: any): size is IconSize => {
  if (!size) return false;
  return iconSizes.some((iconSize) => iconSize === size);
};

export type IconSize = typeof iconSizes[number];

const getIconType = (type?: FontawesomeIconType) => {
  switch (type) {
    case 'solid':
      return 'fas';
    case 'brand':
      return 'fab';
    case 'regular':
    default:
      return 'far';
  }
};

const getIconSize = (size?: IconSize) => {
  switch (size) {
    case 'lg':
    case 'large':
      return '24px';
    case 'md':
    case 'medium':
    default:
      return '16px';
  }
};

/**
 * Wrapper component for Font Awesome icons
 *
 * You can find the list of icons on the [FontAwesome website](https://fontawesome.com/icons)
 *
 * We typically default to the regular type, but you can also use the solid or brand type for icons that are only available in those types. Please contact [Jen (ENG)](https://finleytechnologies.slack.com/team/U02G5HYMA8G) or [Judy (Design)](https://finleytechnologies.slack.com/team/U050EF64N8M) if you want to use an icon that is not available.
 *
 * You can also check our custom Kit in our [Fontawesome Control Panel](https://fontawesome.com/kits/94f609ad73/icons) (shared login available in One Password) for the other custom icons that are available. Please do not update the custom Kit without checking with [Jen (ENG)](https://finleytechnologies.slack.com/team/U02G5HYMA8G) or [Judy (Design)](https://finleytechnologies.slack.com/team/U050EF64N8M) first.
 *
 * @param size "medium" | "large", defaults to 16px
 * @param type "brand" | "regular" | "solid", defaults to regular
 * @param color defaults to black on customer dash and white on admin dash
 */
export const Icon: React.FC<{
  size?: IconSize | string;
  type?: FontawesomeIconType;
  color?: IconProps['color'];
  style?: React.CSSProperties;
  boxSize?: number;
  icon: string;
}> = ({ type, icon, size, color, style, boxSize }) => {
  const iconClass = `fa-${icon}`;

  return (
    <ChakraUiIcon
      style={style}
      fontSize={isIconSize(size) ? getIconSize(size) : size}
      color={color}
      key={icon}
      boxSize={boxSize}
    >
      <i className={`${getIconType(type)} ${iconClass}`} />
    </ChakraUiIcon>
  );
};
